import {
  THEME_ACTIVE_ATTR,
  THEME_COLOR_TOKENS,
  THEME_FONT_STACKS,
  THEME_FONT_VAR_NAME,
  THEME_RADIUS_KEYS,
  getByPath,
  themeColorVarName,
  themeRadiusVarName
} from './tokens';
import type {
  IResolvedTheme,
  ThemeChannelColorKeyType,
  ThemeColorKeyType,
  ThemeGlobalStylesType,
  ThemeRadiusKeyType,
  ThemeVantPresetType
} from '../types';

type ThemeRefType = ThemeColorKeyType | `radius-${ThemeRadiusKeyType}` | 'font-family';

const TOKEN_FORMAT = Object.fromEntries(THEME_COLOR_TOKENS.map(({ key, format }) => [key, format])) as Record<
  ThemeColorKeyType,
  'channels' | 'color'
>;

/**
 * 业务侧引用主题变量（组件字符串 prop / :style）：
 * themeVar('text-tertiary', 0.4) → rgba(var(--h5-theme-text-tertiary-rgb), 0.4)
 * 默认值由 preflight 写在 :root，无需手写 fallback 字面量
 */
export function themeVar(key: ThemeChannelColorKeyType, alpha?: number): string;
export function themeVar(key: Exclude<ThemeRefType, ThemeChannelColorKeyType>): string;
export function themeVar(key: ThemeRefType, alpha?: number): string {
  if (key === 'font-family') return `var(${THEME_FONT_VAR_NAME})`;
  if (key.startsWith('radius-')) return `var(${themeRadiusVarName(key.slice(7) as ThemeRadiusKeyType)})`;
  const format = TOKEN_FORMAT[key as ThemeColorKeyType];
  const name = themeColorVarName(key, format);
  if (format === 'color') return `var(${name})`;
  return alpha === undefined ? `rgb(var(${name}))` : `rgba(var(${name}), ${alpha})`;
}

/** 快照 → 主题根变量（--h5-theme-*）；通道值输出逗号格式，兼容旧内核 */
export function createThemeRootVars(theme: IResolvedTheme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const { key, path, format } of THEME_COLOR_TOKENS) {
    const value = String(getByPath(theme.colors, path));
    vars[themeColorVarName(key, format)] = format === 'channels' ? value.replace(/ /g, ', ') : value;
  }
  for (const key of THEME_RADIUS_KEYS) vars[themeRadiusVarName(key)] = theme.radius[key];
  vars[THEME_FONT_VAR_NAME] = THEME_FONT_STACKS[theme.fontFamily];
  return vars;
}

const VANT_BASIC: Record<string, ThemeRefType> = {
  '--van-primary-color': 'primary',
  '--van-button-primary-background': 'primary',
  '--van-back-top-background': 'primary',
  '--van-text-color': 'text',
  '--van-base-font': 'font-family'
};
const VANT_FULL: Record<string, ThemeRefType> = {
  ...VANT_BASIC,
  '--van-success-color': 'success',
  '--van-warning-color': 'warn',
  '--van-danger-color': 'error',
  '--van-text-color-2': 'text-secondary',
  '--van-text-color-3': 'text-tertiary',
  '--van-tab-text-color': 'text-tertiary',
  '--van-tab-text-color-active': 'text',
  '--van-background': 'bg',
  '--van-background-3': 'neutral',
  '--van-active-color': 'neutral',
  '--van-border-color': 'neutral',
  '--van-overlay-background': 'bg-dark',
  '--van-radius-sm': 'radius-sm',
  '--van-radius-md': 'radius-md',
  '--van-radius-lg': 'radius-lg',
  '--van-popup-round-radius': 'radius-lg',
  '--van-button-primary-border-color': 'primary'
};
const VANT_PRESETS: Record<ThemeVantPresetType, Record<string, ThemeRefType>> = { basic: VANT_BASIC, full: VANT_FULL };

/** 快照 → 运行时写入 root 的全部变量（主题根变量 + Vant 映射，Vant 直接写实值以兼容旧内核） */
export function createThemeCssVars(theme: IResolvedTheme, vant: ThemeVantPresetType): Record<string, string> {
  const vars = createThemeRootVars(theme);
  for (const [name, ref] of Object.entries(VANT_PRESETS[vant])) {
    if (ref === 'font-family') vars[name] = vars[THEME_FONT_VAR_NAME];
    else if (ref.startsWith('radius-')) vars[name] = vars[themeRadiusVarName(ref.slice(7) as ThemeRadiusKeyType)];
    else {
      const format = TOKEN_FORMAT[ref as ThemeColorKeyType];
      const value = vars[themeColorVarName(ref, format)];
      vars[name] = format === 'channels' ? `rgb(${value})` : value;
    }
  }
  return vars;
}

/**
 * 主题全局样式：字体、正文色、shared 组件（btn-main / 圆按钮弹窗）静态色补丁。
 * always：始终生效；runtime：仅运行时主题激活（html[data-h5-host-theme]）后生效。
 * 门槛选择器 :root:root 用于压过 shared index.scss 同名规则（其后加载，且含 :root:root）。
 */
export function createThemeGlobalStyles(mode: ThemeGlobalStylesType): string {
  const gate = mode === 'always' ? ':root:root' : `:root[${THEME_ACTIVE_ATTR}]`;
  const primary = themeVar('primary');
  const font = themeVar('font-family');
  return [
    `${gate}{--van-base-font:${font};}`,
    `${gate} body{color:${themeVar('text')};}`,
    `${gate} body,${gate} body #app,${gate}.android body,${gate}.android body #app{font-family:${font};}`,
    `${gate} button,${gate} input,${gate} textarea,${gate} select{font-family:inherit;}`,
    `${gate} .van-button--primary.btn-main{background-color:${primary};}`,
    `${gate} .van-button--primary.btn-main.cancel,${gate} .van-button--primary.btn-main.van-button--disabled,${gate} .van-button--primary.btn-main.van-button--loading{background-color:${themeVar('bg')};color:${themeVar('text-tertiary')};}`,
    `${gate} .van-dialog.van-dialog--round-button .van-dialog__message,${gate} .van-dialog.van-dialog--round-button .van-dialog__cancel{color:${themeVar('text-tertiary')};}`,
    `${gate} .van-dialog.van-dialog--round-button .van-dialog__confirm{background:${primary};}`
  ].join('\n');
}
